import { FC, useEffect, useMemo, useState } from 'react';
import i18n from 'i18next';

import { notifications } from 'features/userSettings/actions';

import { useAppDispatch, useAppSelector, useTranslate } from 'hooks';

import { NotificationProps } from 'models';

import { FormItem, RadioGroup, SwitchButton } from 'componets/form';
import { SettingsCard } from './SettingsCard';

import s from './index.module.scss';

const FREQUENCY_OPTIONS: any = {
	2: i18n.t('frequencyWeeklyText'),
	3: i18n.t('frequencyMonthlyText'),
};

const VALUES: any = { 2: 'weekly_summary', 3: 'monthly_summary' };

export const SettingNotificationCard = () => {
	const settings = useAppSelector((state) => state.userSettings.entity);

	const [loading, setLoading] = useState(false);
	const [isShow, setIsShow] = useState(settings.account_summary > 0);

	useEffect(() => {
		setIsShow(settings.account_summary > 0);
	}, [settings.account_summary]);

	const {
		notificationText,
		notificationDepositText,
		notificationNewsText,
		accountSummaryText,
		notificationRepaymentText,
	} = useTranslate([
		'notificationText',
		'notificationDepositText',
		'notificationNewsText',
		'accountSummaryText',
		'notificationRepaymentText',
	]);

	const options = useMemo(
		() =>
			Object.keys(FREQUENCY_OPTIONS).map((key) => ({
				value: key,
				label: FREQUENCY_OPTIONS[key],
			})) || [],
		[],
	);

	const dispatch = useAppDispatch();

	const onSubmit = (data: NotificationProps) => {
		dispatch(notifications({ ...data }))
			.unwrap()
			.finally(() => {
				setLoading(false);
			});
	};

	return (
		<SettingsCard title={notificationText}>
			<NotificationRow
				label={notificationDepositText}
				checked={settings.notifications}
				subject="deposit_withdrawal"
			/>

			<NotificationRow label={notificationNewsText} checked={settings.newsletter} subject="news" />

			<div className={s.row}>
				<SwitchButton
					label={accountSummaryText}
					onClick={(data) => {
						if (data) {
							setIsShow(true);
							return;
						}

						if (settings.account_summary > 0) {
							setLoading(true);
							onSubmit({
								monthly_summary: { email: false },
								weekly_summary: { email: false },
							});
							return;
						}

						setIsShow(false);
					}}
					loading={loading}
					checked={isShow}
				/>
			</div>

			{isShow && (
				<FormItem name="account_summary" className={s.radio}>
					<RadioGroup
						isQuiz
						isSettings
						defaultValue={settings.account_summary.toString()}
						options={options}
						onChange={(data) => {
							const value = data[0];
							const subject = VALUES[value];

							const offValue = subject === 'monthly_summary' ? 'weekly_summary' : 'monthly_summary';

							const formData = {
								[subject]: { email: true },
								[offValue]: { email: false },
							};

							onSubmit(formData);
						}}
					/>
				</FormItem>
			)}

			<NotificationRow
				label={notificationRepaymentText}
				checked={settings.repayment}
				subject="repayment"
			/>
		</SettingsCard>
	);
};

const NotificationRow: FC<{
	label: string;
	checked: boolean;
	subject: 'deposit_withdrawal' | 'news' | 'repayment';
}> = ({ label, checked, subject }) => {
	const dispatch = useAppDispatch();

	const [loading, setLoading] = useState(false);

	const onClick = (data: boolean) => {
		setLoading(true);
		dispatch(notifications({ [subject]: { email: data } }))
			.unwrap()
			.finally(() => {
				setLoading(false);
			});
	};

	const isTooltip = subject === 'deposit_withdrawal' && checked;

	return (
		<div className={s.row}>
			<SwitchButton
				label={label}
				onClick={onClick}
				loading={loading}
				checked={checked}
				tooltip={isTooltip ? 'depositWithdrawalTooltipText' : ''}
				disabled={isTooltip}
			/>
		</div>
	);
};
